import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  api,
  type ExecutiveMemoJobCreateData,
  type ExecutiveMemoJobStatusData,
  type ExecutiveMemoRelayStatus,
} from "../lib/api";
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  EXECUTIVE_MEMO_ACCEPTED_IMAGE_TYPES,
  clearPendingExecutiveMemoJobSnapshot,
  createExecutiveMemoClientRequestId,
  dateTimeLocalToIso,
  formatExecutiveMemoStageLabel,
  getBackendMessage,
  getExecutiveMemoFileValidationMessage,
  getExecutiveMemoPollIntervalMs,
  readPendingExecutiveMemoJobSnapshot,
  toDateTimeLocalValue,
  toPendingExecutiveMemoJobNotification,
  writePendingExecutiveMemoJobNotification,
  writePendingExecutiveMemoJobSnapshot,
} from "../lib/executiveMemos";

type ExecutiveMemoJobViewStatus =
  | "idle"
  | "uploading"
  | ExecutiveMemoRelayStatus;

type ExecutiveMemoJobView = {
  status: ExecutiveMemoJobViewStatus;
  jobId?: string;
  clientRequestId?: string;
  acceptedAt?: string | null;
  processedAt?: string | null;
  memoId?: string | null;
  failedStage?: string | null;
  failedError?: string | null;
};

const HINT_MAX_LENGTH = 500;

function isJobProcessing(status: ExecutiveMemoJobViewStatus) {
  return status === "uploading" || status === "accepted" || status === "processing";
}

function isJobTerminal(status: ExecutiveMemoJobViewStatus) {
  return status === "completed" || status === "failed" || status === "timed_out";
}

function getJobHeadline(job: ExecutiveMemoJobView, errorMessage: string | null) {
  if (errorMessage && job.status === "idle") return errorMessage;

  switch (job.status) {
    case "uploading":
      return "社長メモを送信しています。";
    case "accepted":
      return "処理を受け付けました。";
    case "processing":
      return "メモを文字起こし・整理しています。";
    case "completed":
      return "社長メモを保存しました。";
    case "failed":
      return "社長メモの処理に失敗しました。";
    case "timed_out":
      return "処理が時間内に完了しませんでした。";
    default:
      return "";
  }
}

function toJobView(response: ExecutiveMemoJobStatusData): ExecutiveMemoJobView {
  return {
    status: response.relayStatus,
    jobId: response.jobId,
    clientRequestId: response.clientRequestId ?? undefined,
    acceptedAt: response.acceptedAt ?? null,
    processedAt: response.processedAt ?? null,
    memoId: response.memoId ?? null,
    failedStage: response.failedStage ?? null,
    failedError: response.failedError ?? null,
  };
}

export default function RequestExecutiveMemosPage() {
  const navigate = useNavigate();
  const pollTimerRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [hint, setHint] = useState("");
  const [capturedAt, setCapturedAt] = useState("");
  const [clientRequestId, setClientRequestId] = useState(() => createExecutiveMemoClientRequestId());
  const [job, setJob] = useState<ExecutiveMemoJobView>({ status: "idle" });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [jobRefreshing, setJobRefreshing] = useState(false);

  const processing = isJobProcessing(job.status);
  const headline = getJobHeadline(job, errorMessage);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const notifyJobResolved = useCallback((response: ExecutiveMemoJobStatusData) => {
    const notification = toPendingExecutiveMemoJobNotification(response);
    if (!notification) return;

    writePendingExecutiveMemoJobNotification(notification);
  }, []);

  const resetMissingJob = useCallback(() => {
    stopPolling();
    clearPendingExecutiveMemoJobSnapshot();
    setJob({ status: "idle" });
    setErrorMessage("前回のジョブが見つかりませんでした。もう一度依頼できます。");
  }, [stopPolling]);

  const updateJobFromStatus = useCallback(
    (response: ExecutiveMemoJobStatusData) => {
      const nextJob = toJobView(response);

      if (isJobTerminal(response.relayStatus)) {
        setErrorMessage(null);
        clearPendingExecutiveMemoJobSnapshot();
        stopPolling();
        notifyJobResolved(response);
      }

      setJob(nextJob);
      return nextJob;
    },
    [notifyJobResolved, stopPolling]
  );

  const scheduleJobPoll = useCallback(
    (jobId: string, startedAtMs: number, requestId?: string) => {
      stopPolling();
      const intervalMs = getExecutiveMemoPollIntervalMs(Math.max(0, Date.now() - startedAtMs));

      pollTimerRef.current = window.setTimeout(async () => {
        setJobRefreshing(true);
        try {
          const response = await api.getExecutiveMemoJob(jobId);
          const nextJob = updateJobFromStatus(response);

          if (response.relayStatus === "accepted" || response.relayStatus === "processing") {
            setJob(nextJob);
            scheduleJobPoll(jobId, startedAtMs, response.clientRequestId ?? requestId);
          }
        } catch (error) {
          if (error instanceof AppError && error.code === "NOT_FOUND") {
            resetMissingJob();
            return;
          }

          logError(error);
          setJob((prev) => ({
            ...prev,
            status: "processing",
            jobId,
            clientRequestId: requestId ?? prev.clientRequestId,
          }));
          scheduleJobPoll(jobId, startedAtMs, requestId);
        } finally {
          setJobRefreshing(false);
        }
      }, intervalMs);
    },
    [resetMissingJob, stopPolling, updateJobFromStatus]
  );

  const beginPolling = useCallback(
    (response: ExecutiveMemoJobCreateData, requestId: string, startedAt: string) => {
      const startedAtMs = Date.parse(startedAt);
      const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

      writePendingExecutiveMemoJobSnapshot({
        jobId: response.jobId,
        clientRequestId: requestId,
        startedAt: new Date(safeStartedAtMs).toISOString(),
      });

      writePendingExecutiveMemoJobNotification({
        jobId: response.jobId,
        status: "processing",
      });

      setJob({
        status: "accepted",
        jobId: response.jobId,
        clientRequestId: requestId,
        acceptedAt: response.acceptedAt,
      });
      scheduleJobPoll(response.jobId, safeStartedAtMs, requestId);
    },
    [scheduleJobPoll]
  );

  const refreshPendingJob = useCallback(
    async (jobId: string, startedAt: string, requestId?: string) => {
      const startedAtMs = Date.parse(startedAt);
      const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

      stopPolling();
      setJobRefreshing(true);
      setErrorMessage(null);

      try {
        const response = await api.getExecutiveMemoJob(jobId);
        const nextJob = updateJobFromStatus(response);

        if (response.relayStatus === "accepted" || response.relayStatus === "processing") {
          setJob(nextJob);
          scheduleJobPoll(jobId, safeStartedAtMs, response.clientRequestId ?? requestId);
        }
      } catch (error) {
        if (error instanceof AppError && error.code === "NOT_FOUND") {
          resetMissingJob();
          return;
        }

        logError(error);
        setJob({
          status: "processing",
          jobId,
          clientRequestId: requestId,
        });
        scheduleJobPoll(jobId, safeStartedAtMs, requestId);
      } finally {
        setJobRefreshing(false);
      }
    },
    [resetMissingJob, scheduleJobPoll, stopPolling, updateJobFromStatus]
  );

  useEffect(() => {
    const pendingSnapshot = readPendingExecutiveMemoJobSnapshot();
    if (pendingSnapshot?.jobId) {
      setJob({
        status: "processing",
        jobId: pendingSnapshot.jobId,
        clientRequestId: pendingSnapshot.clientRequestId,
      });
      void refreshPendingJob(
        pendingSnapshot.jobId,
        pendingSnapshot.startedAt,
        pendingSnapshot.clientRequestId
      );
    }

    return () => {
      stopPolling();
    };
  }, [refreshPendingJob, stopPolling]);

  useEffect(() => {
    const handleFocus = () => {
      const pendingSnapshot = readPendingExecutiveMemoJobSnapshot();
      if (pendingSnapshot?.jobId && job.status !== "uploading") {
        void refreshPendingJob(
          pendingSnapshot.jobId,
          pendingSnapshot.startedAt,
          pendingSnapshot.clientRequestId
        );
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [job.status, refreshPendingJob]);

  const handleFileSelected: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0] ?? null;

    if (!file) {
      setSelectedFile(null);
      setSelectedFileName("");
      return;
    }

    const validationMessage = getExecutiveMemoFileValidationMessage(file);
    if (validationMessage) {
      setSelectedFile(null);
      setSelectedFileName("");
      setErrorMessage(validationMessage);
      return;
    }

    clearPendingExecutiveMemoJobSnapshot();
    setSelectedFile(file);
    setSelectedFileName(file.name);
    setCapturedAt(toDateTimeLocalValue(new Date(file.lastModified).toISOString()));
    setClientRequestId(createExecutiveMemoClientRequestId());
    setJob({ status: "idle" });
    setErrorMessage(null);
  };

  const handleSubmit = async () => {
    if (processing) return;

    if (!selectedFile) {
      setErrorMessage("画像ファイルを選択してください。");
      return;
    }

    const validationMessage = getExecutiveMemoFileValidationMessage(selectedFile);
    if (validationMessage) {
      setErrorMessage(validationMessage);
      return;
    }

    setErrorMessage(null);
    setJob({ status: "uploading", clientRequestId });

    try {
      const response = await api.createExecutiveMemoJob({
        image: selectedFile,
        clientRequestId,
        capturedAt: dateTimeLocalToIso(capturedAt),
        hint: hint.trim() || undefined,
      });
      beginPolling(response, clientRequestId, response.acceptedAt ?? new Date().toISOString());
      navigate("/executive-memos");
    } catch (error) {
      logError(error);
      if (error instanceof AppError && error.meta.status === 429) {
        setErrorMessage("リクエストが集中しています。少し時間をおいて再試行してください。");
      } else if (error instanceof AppError && error.code === "CONFLICT") {
        setErrorMessage(getBackendMessage(error, ["client_request_id"]) ?? "入力内容を確認して再依頼してください。");
      } else {
        setErrorMessage(getBackendMessage(error, ["image", "hint", "captured_at"]) ?? toUserMessage(error));
      }
      setJob({ status: "idle" });
    }
  };

  const handleRefreshJob = async () => {
    const pendingSnapshot = readPendingExecutiveMemoJobSnapshot();
    const currentJobId = job.jobId ?? pendingSnapshot?.jobId;
    const startedAt = pendingSnapshot?.startedAt ?? new Date().toISOString();
    const requestId = job.clientRequestId ?? pendingSnapshot?.clientRequestId;

    if (!currentJobId) {
      setErrorMessage("確認対象のジョブが見つかりませんでした。");
      return;
    }

    await refreshPendingJob(currentJobId, startedAt, requestId);
  };

  const handlePickAnotherFile = () => {
    clearPendingExecutiveMemoJobSnapshot();
    setJob({ status: "idle" });
    setSelectedFile(null);
    setSelectedFileName("");
    setClientRequestId(createExecutiveMemoClientRequestId());
    setErrorMessage(null);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const retryWithNewRequestId = () => {
    clearPendingExecutiveMemoJobSnapshot();
    setClientRequestId(createExecutiveMemoClientRequestId());
    setJob({ status: "idle" });
    setErrorMessage(null);
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-3xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/request" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← メイに頼むへ
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">社長メモを作成</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div className="mb-6">
            <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">社長メモ</p>
            <h1 className="mt-2 text-2xl font-black tracking-tight text-[#7C5A4E]">社長メモを作成</h1>
            <p className="mt-2 text-sm leading-6 text-[#8A746A]">
              紙のメモ画像をアップロードして、あとから相談や調査に使える経営メモとして保存します。
            </p>
          </div>

          <div className="grid gap-5">
            <div className="rounded-[1.5rem] border border-[#E4D5C8] bg-white px-5 py-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-black text-[#5D4D46]">画像をアップロード</p>
                  <p className="mt-2 text-sm leading-6 text-[#8A746A]">対応形式: JPEG / PNG / WEBP / HEIC / HEIF</p>
                  <p className="text-sm leading-6 text-[#8A746A]">10MBまでアップロードできます。</p>
                  {selectedFileName ? (
                    <p className="mt-2 text-xs font-bold text-[#7C5A4E]">選択中: {selectedFileName}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={openFilePicker}
                  disabled={processing}
                  className="inline-flex items-center justify-center rounded-full bg-[#7C5A4E] px-5 py-3 text-sm font-black text-white transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  画像を選択
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={EXECUTIVE_MEMO_ACCEPTED_IMAGE_TYPES.join(",")}
                onChange={handleFileSelected}
                disabled={processing}
                className="hidden"
              />
            </div>

            <label className="block">
              <span className="mb-2 block text-sm font-bold text-[#5D4D46]">
                ヒント <span className="font-normal text-[#8A746A]">任意</span>
              </span>
              <textarea
                value={hint}
                onChange={(event) => setHint(event.target.value.slice(0, HINT_MAX_LENGTH))}
                disabled={processing}
                maxLength={HINT_MAX_LENGTH}
                rows={4}
                className="block w-full resize-y rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20 disabled:cursor-not-allowed disabled:opacity-60"
              />
              <span className="mt-2 block text-right text-xs text-[#8A746A]">
                {hint.length}/{HINT_MAX_LENGTH}
              </span>
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-bold text-[#5D4D46]">
                撮影日時 <span className="font-normal text-[#8A746A]">任意</span>
              </span>
              <input
                type="datetime-local"
                value={capturedAt}
                onChange={(event) => setCapturedAt(event.target.value)}
                disabled={processing}
                className="block w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
              />
            </label>
          </div>

          {headline ? (
            <div
              role={job.status === "idle" && errorMessage ? "alert" : undefined}
              className={`mt-5 rounded-2xl border px-4 py-3 text-sm ${
                job.status === "failed" || job.status === "timed_out" || (job.status === "idle" && errorMessage)
                  ? "border-[#E8B2A8] bg-[#FFF3F1] text-[#A1493E]"
                  : "border-[#D8E5D2] bg-[#F4FAF1] text-[#3F5C3B]"
              }`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="font-bold">{headline}</p>
                  {job.jobId ? <p className="mt-1 text-xs">jobId: {job.jobId}</p> : null}
                  {job.failedStage ? (
                    <p className="mt-1 text-xs font-bold">
                      失敗箇所: {formatExecutiveMemoStageLabel(job.failedStage)}
                    </p>
                  ) : null}
                  {job.failedError ? <p className="mt-1 leading-6">{job.failedError}</p> : null}
                </div>
                {(job.status === "accepted" || job.status === "processing") ? (
                  <button
                    type="button"
                    onClick={handleRefreshJob}
                    disabled={jobRefreshing}
                    className="rounded-full border border-current/20 bg-white px-3 py-2 text-xs font-bold transition hover:bg-white/80 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {jobRefreshing ? "確認中..." : "状態を再確認"}
                  </button>
                ) : null}
              </div>

              {job.status === "completed" && job.memoId ? (
                <Link
                  to={`/executive-memos/${job.memoId}`}
                  className="mt-3 inline-flex rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#5D4D46]"
                >
                  メモを開く
                </Link>
              ) : null}
            </div>
          ) : null}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={processing || !selectedFile}
              className="rounded-full bg-[#7C5A4E] px-5 py-3 text-sm font-black text-white shadow-[0_12px_28px_rgba(124,90,78,0.16)] transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {job.status === "uploading" ? "送信中..." : processing ? "処理中..." : "社長メモを作成する"}
            </button>

            {job.status === "failed" || job.status === "timed_out" ? (
              <button
                type="button"
                onClick={retryWithNewRequestId}
                className="rounded-full border border-[#D8C0AF] bg-white px-5 py-3 text-sm font-bold text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2]"
              >
                もう一度依頼する
              </button>
            ) : null}

            {job.status === "completed" || job.status === "failed" || job.status === "timed_out" ? (
              <button
                type="button"
                onClick={handlePickAnotherFile}
                className="rounded-full border border-[#D8C0AF] bg-white px-5 py-3 text-sm font-bold text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2]"
              >
                別の画像を送る
              </button>
            ) : null}
          </div>

          <div className="mt-4 flex justify-end">
            <Link to="/executive-memos" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
              社長メモ一覧
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
